import {
  type FilterMeta,
  sortingFns,
  type FilterFn,
  type SortingFn,
  type ColumnDef,
  type RowData
} from '@tanstack/react-table'
import { rankItem, compareItems } from '@tanstack/match-sorter-utils'
import type { ChartDataValue } from '../../../types'

/**
 * Sentinel used to represent a missing (null/undefined) dimension value in filter state.
 * Must match the sentinel used when building filter menu options so that selecting the
 * "no data" option and filtering rows with a null value use the same representation.
 */
export const NULL_FILTER_VALUE = ''

export const oneOfFilter: FilterFn<any> = (row, columnId, filterValue: string[]) => {
  if (filterValue.length === 0) return true
  const rowValue = row.getValue(columnId)
  if (rowValue == null) {
    return filterValue.includes(NULL_FILTER_VALUE)
  }
  if (typeof rowValue === 'string') {
    return filterValue.includes(rowValue)
  }
  if (typeof rowValue === 'number' || typeof rowValue === 'boolean') {
    return filterValue.includes(String(rowValue))
  }
  return false
}

export const fuzzyFilter: FilterFn<RowData> = (row, columnId, value, addMeta) => {
  const rawValue = row.getValue(columnId)
  if (rawValue == null) {
    return false
  }
  let valueToRank = rawValue

  const columnDef = row.getAllCells().find((cell) => cell.column.id === columnId)?.column.columnDef as ColumnDef<
    RowData,
    ChartDataValue
  >

  if (columnDef != null) {
    valueToRank = columnDef.meta?.chartCard?.descriptions?.[rawValue as string]?.label ?? rawValue
  }

  // Rank the item
  const itemRank = rankItem(valueToRank, value)

  // Store the itemRank info
  addMeta({
    itemRank
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

// Define a custom fuzzy sort function that will sort by rank if the row has ranking information
export const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0

  const metaA = rowA.columnFiltersMeta[columnId] as FilterMeta | undefined
  const metaB = rowB.columnFiltersMeta[columnId] as FilterMeta | undefined

  // Only sort by rank if the column has ranking information
  if (metaA?.itemRank != null && metaB?.itemRank != null) {
    dir = compareItems(metaA.itemRank, metaB.itemRank)
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir
}
